import { useEffect, useMemo, useRef } from 'react';
import { useNavigationContext } from './NavigationProvider';
import { BreadcrumbItem, HeaderAction } from './types';

interface UsePageNavigationOptions {
  breadcrumbs?: BreadcrumbItem[];
  headerActions?: HeaderAction[];
}

/** Keys que no deben provocar actualización (referencias inestables como ReactNode) */
const HEADER_ACTION_IGNORE_KEYS = new Set(['component', 'onClick']);

/**
 * Compara breadcrumbs sin re-renderizar por ReactNode inestables.
 * Usa `_key` para labels dinámicos y compara strings directamente.
 */
function breadcrumbArraysEqual(a: BreadcrumbItem[], b: BreadcrumbItem[]): boolean {
  if (a.length !== b.length) return false;

  return a.every((itemA, index) => {
    const itemB = b[index];
    if (!itemB) return false;

    if (itemA.path !== itemB.path) return false;
    if (itemA.title !== itemB.title) return false;
    if (itemA._key !== itemB._key) return false;

    if (typeof itemA.label === 'string' && typeof itemB.label === 'string') {
      return itemA.label === itemB.label;
    }

    // ReactNode: solo `_key` determina cambios; sin `_key` se asume igual (legacy)
    return true;
  });
}

/**
 * Función helper para comparación shallow de arrays de objetos.
 * Para headerActions se ignoran keys como "component" (ReactNode) que se recrean cada render.
 */
function shallowCompareHeaderActions(a: HeaderAction[], b: HeaderAction[]): boolean {
  if (a.length !== b.length) return false;

  return a.every((itemA, index) => {
    const itemB = b[index];
    if (!itemB) return false;

    const keysA = Object.keys(itemA).filter((k) => !HEADER_ACTION_IGNORE_KEYS.has(k));
    const keysB = Object.keys(itemB).filter((k) => !HEADER_ACTION_IGNORE_KEYS.has(k));

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => {
      const k = key as keyof HeaderAction;
      if (typeof itemA[k] === 'function' && typeof itemB[k] === 'function') {
        return itemA[k] === itemB[k];
      }
      return itemA[k] === itemB[k];
    });
  });
}

/**
 * Hook SIMPLE para establecer breadcrumbs y acciones del header
 *
 * - Establece breadcrumbs/acciones al montar
 * - Limpia breadcrumbs/acciones al desmontar
 * - Sin lógica compleja de timing
 * - Optimizado para evitar re-renders innecesarios
 *
 * @example
 * ```tsx
 * const breadcrumbs = useMemo(() => [
 *   { label: 'Mercancías', path: '/merchandise' },
 *   { label: 'Crear' }
 * ], []);
 *
 * usePageNavigation({ breadcrumbs, headerActions });
 * ```
 */
export function usePageNavigation({ breadcrumbs = [], headerActions = [] }: UsePageNavigationOptions = {}) {
  const { setBreadcrumbs, setHeaderActions } = useNavigationContext();

  // 🎯 Referencias para comparación eficiente
  const prevBreadcrumbsRef = useRef<BreadcrumbItem[]>([]);
  const prevHeaderActionsRef = useRef<HeaderAction[]>([]);

  // 🎯 Memoizar solo cuando realmente cambien (comparación shallow; label es ReactNode y se recrea)
  const memoizedBreadcrumbs = useMemo(() => {
    if (breadcrumbArraysEqual(breadcrumbs, prevBreadcrumbsRef.current)) {
      return prevBreadcrumbsRef.current;
    }
    prevBreadcrumbsRef.current = breadcrumbs;
    return breadcrumbs;
  }, [breadcrumbs]);

  const memoizedHeaderActions = useMemo(() => {
    if (shallowCompareHeaderActions(headerActions, prevHeaderActionsRef.current)) {
      return prevHeaderActionsRef.current;
    }
    prevHeaderActionsRef.current = headerActions;
    return headerActions;
  }, [headerActions]);

  // 🎯 Establecer al montar, limpiar al desmontar
  useEffect(() => {
    // Establecer inmediatamente
    setBreadcrumbs(memoizedBreadcrumbs);
    setHeaderActions(memoizedHeaderActions);

    // Limpiar al desmontar
    return () => {
      setBreadcrumbs([]);
      setHeaderActions([]);
    };
  }, [memoizedBreadcrumbs, memoizedHeaderActions, setBreadcrumbs, setHeaderActions]);

  return {
    setBreadcrumbs,
    setHeaderActions,
  };
}
